/** Shared browsing location. An area override never changes the delivery postcode. */
export const LOCATION_KEY = "hub_location_v1";
export const ALL_AYRSHIRE = "All Towns";
export interface LocationTown { name: string; lat: number; lon: number; borough?: string }
export interface HubLocation {
  version: 1;
  postcode: string | null;
  lat: number | null;
  lon: number | null;
  postcodeTown: string | null;
  browseTown: string;
  mode: "postcode" | "area" | "all";
}
export const emptyLocation = (): HubLocation => ({ version: 1, postcode: null, lat: null, lon: null, postcodeTown: null, browseTown: ALL_AYRSHIRE, mode: "all" });
export function formatPostcode(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const pc = value.replace(/\s/g, "").toUpperCase();
  if (!/^(?:GIR0AA|[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2})$/.test(pc)) return null;
  return pc.slice(0, -3) + " " + pc.slice(-3);
}
export function readLocation(raw: string | null, legacyTown = ALL_AYRSHIRE, legacyPostcode?: unknown): HubLocation {
  try {
    const x = JSON.parse(raw ?? "null");
    if (x?.version === 1 && ["postcode", "area", "all"].includes(x.mode) && typeof x.browseTown === "string" && x.browseTown.length <= 100) {
      const pc = formatPostcode(x.postcode);
      const geo = pc && Number.isFinite(x.lat) && Number.isFinite(x.lon) && x.lat >= 49 && x.lat <= 61 && x.lon >= -9 && x.lon <= 2;
      const postcodeTown = geo && typeof x.postcodeTown === "string" ? x.postcodeTown : null;
      const mode = x.mode === "postcode" && !postcodeTown ? "all" : x.mode;
      return { version: 1, postcode: pc, lat: geo ? x.lat : null, lon: geo ? x.lon : null, postcodeTown, mode, browseTown: mode === "all" ? ALL_AYRSHIRE : mode === "postcode" ? postcodeTown! : x.browseTown };
    }
  } catch { /* migrate malformed or old preferences safely */ }
  return { ...emptyLocation(), postcode: formatPostcode(legacyPostcode), browseTown: legacyTown, mode: legacyTown === ALL_AYRSHIRE ? "all" : "area" };
}
export function distanceKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }) {
  const rad = (n: number) => n * Math.PI / 180;
  const s = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lon - a.lon) / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}
export function browseArea(location: HubLocation, town: string): HubLocation {
  return { ...location, mode: town === ALL_AYRSHIRE ? "all" : "area", browseTown: town };
}
export function usePostcodeArea(location: HubLocation): HubLocation {
  return location.postcodeTown ? { ...location, mode: "postcode", browseTown: location.postcodeTown } : location;
}
export function nearbyTownParam(location: HubLocation, towns: LocationTown[]): string | undefined {
  if (location.browseTown === ALL_AYRSHIRE) return undefined;
  const origin = location.mode === "postcode" && location.lat != null && location.lon != null ? { lat: location.lat, lon: location.lon } : towns.find(t => t.name === location.browseTown);
  if (!origin) return location.browseTown;
  return [...new Set([location.browseTown, ...towns.filter(t => distanceKm(origin, t) <= 12).map(t => t.name)])].join(",");
}
export function locationLabel(location: HubLocation) {
  return location.mode === "postcode" ? `${location.postcode} · ${location.browseTown}` : location.browseTown === ALL_AYRSHIRE ? "All Ayrshire" : `${location.browseTown}${location.postcode ? "" : " · Add postcode"}`;
}
export async function lookupLocationPostcode(value: string, towns: LocationTown[], signal?: AbortSignal, fetcher: typeof fetch = fetch): Promise<HubLocation> {
  const postcode = formatPostcode(value);
  if (!postcode) throw new Error("Enter a full UK postcode, for example KA1 1AA.");
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  const timer = setTimeout(abort, 8000);
  try {
    const response = await fetcher(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.replace(/\s/g, ""))}`, { signal: controller.signal });
    if (response.status === 404) throw new Error("Postcode not found. Check it and try again.");
    if (!response.ok) throw new Error("Postcode lookup is unavailable. Try again or choose an area.");
    const data = await response.json() as { result?: { postcode?: string; latitude?: number; longitude?: number; admin_district?: string } };
    const p = data.result;
    if (!p || !Number.isFinite(p.latitude) || !Number.isFinite(p.longitude) || !formatPostcode(p.postcode)) throw new Error("Postcode lookup returned an invalid location. Please try again.");
    if (!["North Ayrshire", "East Ayrshire", "South Ayrshire"].includes(p.admin_district ?? "")) throw new Error("That postcode is outside Ayrshire. Choose All Ayrshire to browse, or enter an Ayrshire postcode.");
    const coords = { lat: p.latitude!, lon: p.longitude! };
    const nearest = towns.reduce<LocationTown | null>((best, t) => !best || distanceKm(coords, t) < distanceKm(coords, best) ? t : best, null);
    if (!nearest) throw new Error("Areas are still loading. Please try again.");
    return { version: 1, postcode: formatPostcode(p.postcode), ...coords, postcodeTown: nearest.name, browseTown: nearest.name, mode: "postcode" };
  } catch (error) {
    if (controller.signal.aborted) throw new Error("Postcode lookup timed out. Try again or choose an area.");
    throw error;
  } finally { clearTimeout(timer); signal?.removeEventListener("abort", abort); }
}

/** Never pair a newly selected postcode with a different saved street address. */
export function checkoutAddressSeed(addresses: Array<{ address: string; postcode: string }>, selectedPostcode: string | null) {
  const postcode = formatPostcode(selectedPostcode);
  const matching = postcode ? addresses.find(a => formatPostcode(a.postcode) === postcode) : addresses[0];
  return { address: matching?.address ?? "", postcode: postcode ?? matching?.postcode ?? "" };
}
